import { skipToken, useQuery } from "@apollo/client/react";
import { Badge, Variant } from "@leafygreen-ui/badge";
import { InlineCode } from "@leafygreen-ui/typography";
import TaskStatusBadge from "@evg-ui/lib/components/Badge/TaskStatusBadge";
import TestStatusBadge from "@evg-ui/lib/components/Badge/TestStatusBadge";
import Icon from "@evg-ui/lib/components/Icon";
import { StyledLink } from "@evg-ui/lib/components/styles";
import { usePageTitle } from "@evg-ui/lib/hooks/usePageTitle";
import { TaskStatus } from "@evg-ui/lib/types/task";
import { shortenGithash, trimStringFromMiddle } from "@evg-ui/lib/utils/string";
import { usePreferencesAnalytics } from "analytics";
import Breadcrumbs from "components/Breadcrumbs";
import { LogTypes } from "constants/enums";
import { getEvergreenTaskURL } from "constants/externalURLTemplates";
import {
  ExecutionPlatform,
  TestLogUrlAndRenderingTypeQuery,
  TestLogUrlAndRenderingTypeQueryVariables,
} from "gql/generated/types";
import { GET_TEST_LOG_URL_AND_RENDERING_TYPE } from "gql/queries";
import { useTaskQuery } from "hooks/useTaskQuery";

interface Props {
  execution: number;
  fileName?: string;
  groupID?: string;
  logType?: LogTypes;
  taskID: string;
  testID?: string;
}

export const EvergreenTaskSubHeader: React.FC<Props> = ({
  execution,
  fileName,
  groupID,
  logType,
  taskID,
  testID,
}) => {
  const { sendEvent } = usePreferencesAnalytics();
  const { loading: isLoadingTask, task: taskData } = useTaskQuery({
    execution,
    taskID,
  });
  const { data: testData, loading: isLoadingTest } = useQuery<
    TestLogUrlAndRenderingTypeQuery,
    TestLogUrlAndRenderingTypeQueryVariables
  >(
    GET_TEST_LOG_URL_AND_RENDERING_TYPE,
    logType === LogTypes.EVERGREEN_TEST_LOGS && testID
      ? {
          variables: {
            execution,
            taskID,
            testName: `^${testID}$`,
          },
        }
      : skipToken,
  );

  let currentTest: { testFile: string; status: string } | null = null;
  if (logType === LogTypes.EVERGREEN_TEST_LOGS) {
    currentTest = testData?.task?.tests?.testResults?.[0] ?? null;
  }
  let pageTitle = `Task logs for ${taskData?.displayName}`;
  if (currentTest) {
    pageTitle = `Test Logs for ${currentTest?.testFile}`;
  }
  usePageTitle(pageTitle);

  if (isLoadingTask || isLoadingTest || !taskData) {
    return (
      <>
        <Icon glyph="EvergreenLogo" size={24} />
        <StyledLink
          href={getEvergreenTaskURL(taskID, execution)}
          onClick={() => sendEvent({ name: "Clicked task link" })}
        >
          Task Page
        </StyledLink>
      </>
    );
  }
  const {
    displayName,
    displayStatus,
    execution: taskExecution,
    executionPlatform,
    patchNumber,
    versionMetadata,
  } = taskData;

  const { isPatch, message, projectMetadata, revision } = versionMetadata;
  const projectIdentifier = projectMetadata?.identifier;

  const breadcrumbs = [
    {
      "data-testid": "project-breadcrumb",
      text: projectIdentifier,
    },
    {
      "data-testid": "version-breadcrumb",
      text: isPatch ? (
        `Patch ${patchNumber}`
      ) : (
        <InlineCode>{shortenGithash(revision)}</InlineCode>
      ),
      tooltipText: message,
    },
    {
      "data-testid": "task-breadcrumb",
      href: getEvergreenTaskURL(taskID, taskExecution),
      onClick: () => {
        sendEvent({ name: "Clicked task link" });
      },
      text: (
        <>
          {trimStringFromMiddle(displayName, 30)}{" "}
          <TaskStatusBadge status={displayStatus as TaskStatus} />{" "}
          {executionPlatform === ExecutionPlatform.Container && (
            <Badge
              data-testid="task-execution-platform-badge"
              variant={Variant.Blue}
            >
              Container
            </Badge>
          )}
        </>
      ),
      tooltipText: displayName.length > 30 && displayName,
    },
    ...(testID
      ? [
          {
            "data-testid": "test-breadcrumb",
            text: (
              <>
                {trimStringFromMiddle(currentTest?.testFile ?? "Test", 80)}{" "}
                <TestStatusBadge status={currentTest?.status} />
              </>
            ),
            tooltipText:
              currentTest &&
              currentTest.testFile.length > 80 &&
              currentTest.testFile,
          },
        ]
      : []),
    ...(fileName
      ? [
          {
            "data-testid": "file-breadcrumb",
            text: fileName,
          },
        ]
      : []),
    ...(groupID
      ? [
          {
            "data-testid": "group-breadcrumb",
            text: groupID,
          },
        ]
      : []),
  ];

  return (
    <>
      <Icon glyph="EvergreenLogo" size={24} />
      <Breadcrumbs breadcrumbs={breadcrumbs} />
    </>
  );
};
